import type { ExperienceListFilters } from 'actions/ExperienceActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { arrayToObject, groupBy, max, min, unique } from 'lib/array/arrayUtils'
import { getPlaceByRegionCode } from 'constants/places'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import moment from 'moment'
import {
  ASPIRE_LOUNGE_IDS,
  COLLINSON_AU_LOUNGE_IDS,
  COLLINSON_NZ_LOUNGE_IDS,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  LOUNGE_PASS_ACCOMMODATION_DISTANCE_RANGE,
} from 'constants/experience'
import { getExperienceListKey } from './experienceUtils'

interface BuildBedbankFiltersProps {
  bedbankItems: Array<App.Checkout.BedbankAccommodationOfferView> | Array<App.OrderBedbankItem>
}
function buildBedbankFilters(props: BuildBedbankFiltersProps): Array<Partial<ExperienceListFilters>> {
  const { bedbankItems } = props

  return bedbankItems.map(item => {
    const isOrderItem = 'id' in item

    const startDate = isOrderItem ? moment(item.checkIn).format(ISO_DATE_FORMAT) : item.startDate
    const endDate = isOrderItem ? moment(item.checkOut).format(ISO_DATE_FORMAT) : item.endDate
    const latitude = item.offer?.property?.latitude
    const longitude = item.offer?.property?.longitude

    return {
      from: startDate,
      to: endDate,
      latitude,
      longitude,
    }
  })
}

interface BuildTourFiltersProps {
  tourItems: Array<App.Checkout.TourV2AccommodationOfferView> | Array<App.Tours.TourV2OrderItem>
  offers?: App.OfferState['offers']
}
function buildTourFilters(props: BuildTourFiltersProps): Array<Partial<ExperienceListFilters>> {
  const { tourItems, offers } = props

  if (!offers) return []

  return tourItems.map(item => {
    const isOrderItem = 'id' in item

    if (isOrderItem) {
      /**
         * @NOTE - temp implementation to get the locations visited details from the offer.
         * Tour orders don't return the locations visited details, so we need to get the offer
         * and get the variation from the offer.
         */
      const offer = offers[item.tourId]

      if (!offer || !isTourV2Offer(offer)) {
        return []
      }

      const tourOffer: App.Tours.TourV2Offer = offer

      const itemVariationId = item.tourOptionId
      const tourOfferVariations = Object.values(tourOffer.variations)
      const offerVariation = tourOfferVariations.find(variation => variation.fkTourOptionId === itemVariationId)

      if (!offerVariation) {
        return []
      }

      const firstItineraryLocation = min(offerVariation.itinerary, (itinerary) => itinerary.startDay)?.locationsVisitedDetails[0]
      const lastItineraryLocation = max(offerVariation.itinerary, (itinerary) => itinerary.startDay)?.locationsVisitedDetails[0]

      return [
        {
          from: item.tour.startDate,
          to: item.tour.endDate,
          latitude: firstItineraryLocation?.latitude,
          longitude: firstItineraryLocation?.longitude,
        },
        {
          from: item.tour.startDate,
          to: item.tour.endDate,
          latitude: lastItineraryLocation?.latitude,
          longitude: lastItineraryLocation?.longitude,
        },
      ]
    } else {
      return item.itemViews.map(itemView => {
        const firstItineraryLocation = min(itemView.variation?.itinerary ?? [], (item) => item.startDay)?.locationsVisitedDetails[0]
        const lastItineraryLocation = max(itemView.variation?.itinerary ?? [], (item) => item.startDay)?.locationsVisitedDetails[0]
        return [
          {
            from: itemView.item.startDate,
            to: itemView.item.endDate,
            latitude: firstItineraryLocation?.latitude,
            longitude: firstItineraryLocation?.longitude,
          },
          {
            from: itemView.item.startDate,
            to: itemView.item.endDate,
            latitude: lastItineraryLocation?.latitude,
            longitude: lastItineraryLocation?.longitude,
          },
        ]
      })
    }
  }).flat(2)
}

interface BuildHotelFiltersProps {
  hotelItems: Array<App.Checkout.LEAccommodationOfferView> | Array<App.OrderItem>
}
function buildHotelFilters(props: BuildHotelFiltersProps): Array<Partial<ExperienceListFilters>> {
  const { hotelItems } = props

  return hotelItems.map(item => {
    const isOrderItem = 'id' in item

    const startDate = isOrderItem ? item.reservation?.startDate : item.startDate
    const endDate = isOrderItem ? item.reservation?.endDate : item.endDate
    const latitude = item.offer?.property?.latitude
    const longitude = item.offer?.property?.longitude

    return {
      from: startDate,
      to: endDate,
      latitude,
      longitude,
    }
  })
}

interface BuildVillaFiltersProps {
  villaItemsViews?: Array<App.Checkout.VillaItemView>
}
function buildVillaFilters(props: BuildVillaFiltersProps): Array<Partial<ExperienceListFilters>> {
  const { villaItemsViews } = props

  if (!villaItemsViews) return []

  return villaItemsViews.map(view => {
    return {
      from: view.item.checkIn,
      to: view.item.checkOut,
      latitude: view.offer?.property?.latitude,
      longitude: view.offer?.property?.longitude,
    }
  })
}

interface BuildFlightFiltersProps {
  flightItems: Array<App.Checkout.FlightItemView> | Array<App.OrderFlightItem>
  airports: Array<App.Airport>
  experiences: App.ExperienceState['experiences']
  experienceLists: App.ExperienceListState
  isSpoofed: boolean
  flightDetailsByOrderItems?: Record<string, App.FlightOrderItemDetails>
  closestAirport?: App.Airport
  regionCode: string
  recentlyUsedAirportCode?: string,
  accommodationFilters: Array<Partial<ExperienceListFilters>>
}
function buildFlightFilters(props: BuildFlightFiltersProps): Array<Partial<ExperienceListFilters>> {
  const { flightItems, airports, experiences, experienceLists, isSpoofed, flightDetailsByOrderItems, closestAirport, regionCode, recentlyUsedAirportCode, accommodationFilters } = props

  const airportsByCode = arrayToObject(airports, airport => airport.code)
  // Build flight filters for departing and arriving flights
  // E.g.: SYD (2025-09-16) -> MEL (2025-09-16), MEL (2025-09-18) -> SYD (2025-09-18)
  const flightFilters: Array<Partial<ExperienceListFilters>> = flightItems.map(item => {
    const isOrderItem = 'id' in item

    let flights: Array<App.LegFlight> | Array<App.Flight>

    if (isOrderItem) {
      const flightDetail = flightDetailsByOrderItems?.[item.id]
      if (!flightDetail?.bookingJourney?.legs?.length) return []

      flights = flightDetail.bookingJourney.legs.flatMap(leg => leg.flights ?? [])
    } else {
      flights = item.flights.map(flight => flight.journeyFlight.flights).flat()
    }

    return flights.map(flight => {
      const departingAirport = 'departingAirport' in flight ? flight.departingAirport : flight.departureAirport
      const arrivingAirport = flight.arrivalAirport

      return [
        {
          from: moment(flight.departingDate).format(ISO_DATE_FORMAT),
          to: moment(flight.arrivalDate).format(ISO_DATE_FORMAT),
          latitude: airportsByCode[departingAirport]?.latitude,
          longitude: airportsByCode[departingAirport]?.longitude,
          distanceRange: ['0KM', '10KM'],
        },
        {
          from: moment(flight.departingDate).format(ISO_DATE_FORMAT),
          to: moment(flight.arrivalDate).format(ISO_DATE_FORMAT),
          latitude: airportsByCode[arrivingAirport]?.latitude,
          longitude: airportsByCode[arrivingAirport]?.longitude,
          distanceRange: ['0KM', '10KM'],
        },
      ]
    })
  }).flat(2)

  // If there are no flights in the cart, add the closest airport
  if (!flightFilters.length) {
    const accommodationsDateRange = {
      from: min(accommodationFilters.map(filter => filter.from)),
      to: max(accommodationFilters.map(filter => filter.to)),
    }

    // If customer is being spoofed, use the customer's region lounges rather than
    // the agent's location, and append known unlisted loyalty lounge IDs.
    if (isSpoofed) {
      const currentPlace = getPlaceByRegionCode(regionCode)
      const listKey = getExperienceListKey({ placeId: currentPlace.id, categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID], sortBy: 'type' })
      const customerLoungeList = experienceLists[listKey]

      // Collinson AU/NZ and Aspire lounges are unlisted and absent from the search index.
      const customerLoungeIds = customerLoungeList?.ids.filter(id => {
        const tickets = (experiences[id] as any)?.tickets as Array<{ isLoyaltyEligible?: boolean }> | undefined
        return tickets?.some(ticket => ticket.isLoyaltyEligible)
      }) ?? []
      const loungeIds = unique([
        ...customerLoungeIds,
        ...COLLINSON_AU_LOUNGE_IDS,
        ...COLLINSON_NZ_LOUNGE_IDS,
        ...ASPIRE_LOUNGE_IDS,
      ])

      if (loungeIds.length) {
        flightFilters.push({
          from: accommodationsDateRange.from,
          to: accommodationsDateRange.to,
          experienceIds: loungeIds,
          distanceRange: undefined,
        })
      }
    } else {
      // If regular user (no spoof) and there is no closest airport,
      // use the recently used airport (when available)
      let referenceAirport = closestAirport
      if (!referenceAirport && recentlyUsedAirportCode) {
        referenceAirport = airportsByCode[recentlyUsedAirportCode]
      }
      if (referenceAirport) {
        flightFilters.push({
          from: accommodationsDateRange.from,
          to: accommodationsDateRange.to,
          latitude: referenceAirport.latitude,
          longitude: referenceAirport.longitude,
          distanceRange: ['0KM', '10KM'],
        })
      }
    }
  }

  return flightFilters
}

interface Props {
  bedbankItems: Array<App.Checkout.BedbankAccommodationOfferView> | Array<App.OrderBedbankItem>,
  tourItems: Array<App.Checkout.TourV2AccommodationOfferView> | Array<App.Tours.TourV2OrderItem>,
  hotelItems: Array<App.Checkout.LEAccommodationOfferView> | Array<App.OrderItem>,
  flightItems: Array<App.Checkout.FlightItemView> | Array<App.OrderFlightItem>,
  airports: Array<App.Airport>,
  experiences: App.ExperienceState['experiences'],
  experienceLists: App.ExperienceListState,
  isSpoofed: boolean,
  regionCode: string,
  villaItemsViews?: Array<App.Checkout.VillaItemView>,
  flightDetailsByOrderItems?: Record<string, App.FlightOrderItemDetails>,
  recentlyUsedAirportCode?: string,
  offers?: App.OfferState['offers'],
  closestAirport?: App.Airport,
  cruiseItems?: Array<App.OrderCruiseItem>,
  cruiseOfferSnapshots?: Record<string, App.Cruises.CruiseOfferSnapshot>,
}

/**
 * Builds the loyalty lounge pass filters used to surface **airport lounge passes**
 * for both checkout and order view.
 *
 * Business logic overview:
 * - **Goal**: fetch the most relevant lounges for the customer's trip based on what’s
 *   in the cart/order.
 * - **Where the signal comes from**: date range + locations inferred from each item type
 *   (accommodation coordinates/dates, tour start/end + visited locations, flight departure/arrival airports).
 * - **Primary behaviour (when flights exist)**: create lounge searches around each
 *   departure/arrival airport for the corresponding travel dates (10km radius), so lounges
 *   near airports show up.
 * - **Accommodation searches**: 80km around hotel/tour/cruise coordinates, because
 *   property pins are not co-located with airports (e.g. Ginza → NRT is ~58km).
 * - **Fallback (when there are no flights)**:
 *   - If the customer is **spoofed**, we can’t trust agent location, so we fall back to
 *     “all lounges in the current region” for the overall stay date range.
 *   - Otherwise, use the **closest airport** (or the **recently used airport** when available)
 *     for the overall stay date range, to still show nearby lounge options.
 * - **Final output**: merges overlapping filters by location, expands to the full date range per location,
 *   and enforces loyalty-eligible lounge category filters.
 *
 * @param props - Cart/order context (items, airports, region/spoof info, and supporting offer/list data).
 * @returns Filters to request loyalty-eligible airport lounge pass experiences.
 */
function buildCruiseFilters(cruiseItems?: Array<App.OrderCruiseItem>, cruiseOfferSnapshots?: Record<string, App.Cruises.CruiseOfferSnapshot>): Array<Partial<ExperienceListFilters>> {
  if (!cruiseItems?.length || !cruiseOfferSnapshots) return []

  return cruiseItems
    .filter(item => item.status !== 'cancelled')
    .flatMap(item => {
      const snapshot = cruiseOfferSnapshots[item.bookingId]
      if (!snapshot) return []
      const departurePort = snapshot.itineraries?.[0]?.port
      if (!departurePort?.latitude || !departurePort?.longitude) return []
      return [{
        from: moment(snapshot.departureDate).format(ISO_DATE_FORMAT),
        to: moment(snapshot.returnDate).format(ISO_DATE_FORMAT),
        latitude: parseFloat(departurePort.latitude),
        longitude: parseFloat(departurePort.longitude),
      }]
    })
}

function buildLoyaltyLoungePassFilters(props: Props): Array<ExperienceListFilters> {
  const {
    bedbankItems,
    tourItems,
    hotelItems,
    villaItemsViews,
    flightItems,
    offers,
    airports,
    experiences,
    experienceLists,
    flightDetailsByOrderItems,
    isSpoofed,
    regionCode,
    recentlyUsedAirportCode,
    closestAirport,
    cruiseItems,
    cruiseOfferSnapshots,
  } = props

  const bedbankFilters = buildBedbankFilters({ bedbankItems })
  const tourFilters = buildTourFilters({ tourItems, offers })
  const hotelFilters = buildHotelFilters({ hotelItems })
  const villaFilters = buildVillaFilters({ villaItemsViews })
  const cruiseFilters = buildCruiseFilters(cruiseItems, cruiseOfferSnapshots)

  const accommodationFilters = [...bedbankFilters, ...tourFilters, ...hotelFilters, ...villaFilters, ...cruiseFilters]
  const flightFilters = buildFlightFilters({ flightItems, airports, experiences, experienceLists, isSpoofed, flightDetailsByOrderItems, closestAirport, regionCode, recentlyUsedAirportCode, accommodationFilters })

  const allValidFilters = [
    ...flightFilters,
    ...hotelFilters,
    ...villaFilters,
    ...tourFilters,
    ...bedbankFilters,
    ...cruiseFilters,
  ].filter(filter =>
    (!!filter.from && !!filter.to) &&
    ((!!filter.latitude && !!filter.longitude) || !!filter.experienceIds),
  )

  // Group all filters by location and get the date range for each
  const filtersGroupedByLocation = Array.from(groupBy(allValidFilters, (filter) => `${filter.latitude},${filter.longitude}`).values())
  const filters = filtersGroupedByLocation.map(filters => {
    const experienceIds = filters.map(filter => filter.experienceIds).flat().filter(Boolean)

    return {
      from: min(filters.map(filter => filter.from)),
      to: max(filters.map(filter => filter.to)),
      latitude: filters[0].latitude,
      longitude: filters[0].longitude,
      distanceRange: 'distanceRange' in filters[0] ? filters[0].distanceRange : LOUNGE_PASS_ACCOMMODATION_DISTANCE_RANGE,
      categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
      isLoyaltyEligible: true,
      showUnlisted: true,
      ...(experienceIds.length > 0 ? { experienceIds } : {}),
    } as ExperienceListFilters
  })

  return filters
}

export default buildLoyaltyLoungePassFilters
